import {
  setUserBranchesCache,
  primeUserBranchesCache,
  getUserBranchesCacheIfFresh,
  clearUserBranchesCache,
} from '../infra/branches/branchesMemoryCache';
import {
  type BranchAccess,
  type BranchPermission,
  defaultPermissions,
  type ModuleKey,
  useSessionStore,
} from '../../shell/session/store/useSessionStore';
import { parseScaleBarcodeProfile as parseScaleBarcodeProfileShared } from '@shared/barcode';
import { supabase, ensureFreshAccessToken } from '../../shared/supabase/supabaseClient';
import { resolveSessionOrganizationId } from '../infra/branches/branchesRepo';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { useAuthStore } from '../store/useAuthStore';
import { clearLegacyLocalStockSalePolicy } from '../../shell/session/settingsRepo';
import { getOrganizationEntitlementsCacheIfFresh } from '../../shell/feature-flags/planEntitlementsService';
import { debugServiceFetchWarn } from '../../shared/dev/debugServiceFetches';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';
import { resolveSessionEmployee } from './sessionEmployeeResolver';
import { SESSION_CACHE_MS } from '../../shell/session/sessionCacheTtl';

export { setUserBranchesCache, primeUserBranchesCache, getUserBranchesCacheIfFresh, clearUserBranchesCache };

const MODULE_ORDER: ModuleKey[] = [
  'POS',
  'Catalog',
  'Inventory',
  'Customers',
  'Reports',
  'BranchSettings',
  'Promotions',
  'Expenses',
  'Agenda',
  'Cash',
  'Checks',
  'Orders',
  'Tables',
  'Kitchen',
  'MenuOptions',
  'Team',
  'Organization',
  'Billing',
  'Support',
];

function parsePermissionActions(raw: unknown): Record<string, boolean> | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === true || value === 'true') out[key] = true;
    else if (value === false || value === 'false') out[key] = false;
  }
  return out;
}

function parseManualDiscountNumber(raw: unknown, fallback: number): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function parseStockSalePolicy(raw: unknown): BranchAccess['stockSalePolicy'] {
  if (raw === 'warn' || raw === 'allow') return raw;
  return 'block';
}

export function parseSellableModifierAssumption(
  raw: unknown,
): NonNullable<BranchAccess['sellableModifierAssumption']> {
  return raw === 'worst_case' ? 'worst_case' : 'base';
}

export function parseBarcodeInputMode(raw: unknown): BranchAccess['barcodeInputMode'] {
  if (raw === 'camera' || raw === 'hardware' || raw === 'both') return raw;
  return 'both';
}

export function parseScaleBarcodeProfile(raw: unknown): NonNullable<BranchAccess['scaleBarcodeProfile']> {
  return parseScaleBarcodeProfileShared(raw);
}

export function parseKitchenFireMode(raw: unknown): BranchAccess['kitchenFireMode'] {
  return raw === 'before_payment' ? 'before_payment' : 'after_payment';
}

export function parseKitchenAutoArchiveOnDelivered(raw: unknown): boolean {
  return raw !== false;
}

function parseBranchIsActive(raw: unknown): boolean {
  return raw !== false;
}

const mapBranches = (rows: any[] | null): BranchAccess[] => {
  if (!rows) return [];
  return rows.map((branch) => ({
    id: branch.id,
    name: branch.name,
    location: branch.address ?? 'Dirección no cargada',
    latitude: typeof branch.latitude === 'number' ? branch.latitude : branch.latitude ?? null,
    longitude: typeof branch.longitude === 'number' ? branch.longitude : branch.longitude ?? null,
    permissions: defaultPermissions,
    isActive: parseBranchIsActive(branch.is_active),
    stockSalePolicy: parseStockSalePolicy(branch.stock_sale_policy),
    barcodeInputMode: parseBarcodeInputMode(branch.barcode_input_mode),
    scaleBarcodeProfile: parseScaleBarcodeProfile(branch.scale_barcode_profile),
    manualDiscountMaxPercent: parseManualDiscountNumber(branch.manual_discount_max_percent, 20),
    manualDiscountMaxAmount: parseManualDiscountNumber(branch.manual_discount_max_amount, 5000),
    consolidateCartLines: branch.consolidate_cart_lines !== false,
    kitchenFireMode: parseKitchenFireMode(branch.kitchen_fire_mode),
    kitchenAutoArchiveOnDelivered: parseKitchenAutoArchiveOnDelivered(
      branch.kitchen_auto_archive_on_delivered,
    ),
    sellableModifierAssumption: parseSellableModifierAssumption(branch.sellable_modifier_assumption),
  }));
};

const BRANCH_SELECT_COLUMNS =
  'id,name,address,latitude,longitude,stock_sale_policy,sellable_modifier_assumption,barcode_input_mode,scale_barcode_profile,manual_discount_max_percent,manual_discount_max_amount,consolidate_cart_lines,kitchen_fire_mode,kitchen_auto_archive_on_delivered,is_active';

async function fetchActiveBranchesForOrganization(organizationId: string): Promise<BranchAccess[]> {
  const { data, error } = await supabase
    .from('branches')
    .select(BRANCH_SELECT_COLUMNS)
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return mapBranches(data);
}

/**
 * Dueño de organización: sucursales de su tenant (filtrado explícito por organization_id).
 * Empleado: solo sucursales con filas en employee_branch_permissions + permisos reales por módulo.
 *
 * Importante: no hacer `.from('branches')` sin filtro de org. La RLS de authenticated también
 * permite leer sucursales con `public_store_profiles.is_public` (portal), y eso contaminaría
 * el selector de "Elegí sucursal" con locales ajenos.
 */
async function fetchBranchesForSessionUser(userId: string): Promise<BranchAccess[]> {
  const entitlements = getOrganizationEntitlementsCacheIfFresh();
  if (entitlements?.is_owner === true && entitlements.organization_id) {
    recordStartupNetworkCall('branches', 'branches?owner');
    return fetchActiveBranchesForOrganization(entitlements.organization_id);
  }

  recordStartupNetworkCall('branches', 'organizations?id');
  const ownerOrgId = await resolveSessionOrganizationId(userId).catch((err: unknown) => {
    if (isDatabaseUnavailableError(err)) {
      console.warn('[fetchUserBranches] owner check (offline)', err instanceof Error ? err.message : err);
      return null;
    }
    throw err;
  });
  if (ownerOrgId) {
    recordStartupNetworkCall('branches', 'branches?owner');
    return fetchActiveBranchesForOrganization(ownerOrgId);
  }

  const sessionEmp = await resolveSessionEmployee(userId);
  if (!sessionEmp.isEmployee || !sessionEmp.employeeId) {
    recordStartupNetworkCall('branches', 'branches?empty');
    return [];
  }

  recordStartupNetworkCall('branches', 'employee_branch_permissions');
  const { data: permRows, error: permErr } = await supabase
    .from('employee_branch_permissions')
    .select('branch_id, module, can_view, can_edit, can_void, can_discount, can_manage_agenda, can_view_others, actions')
    .eq('employee_id', sessionEmp.employeeId);
  if (permErr) throw permErr;
  if (!permRows?.length) return [];

  const byBranch = new Map<
    string,
    {
      module: string;
      can_view: boolean;
      can_edit: boolean;
      can_void: boolean;
      can_discount: boolean;
      can_manage_agenda: boolean;
      can_view_others: boolean;
      actions?: Record<string, boolean>;
    }[]
  >();
  for (const r of permRows) {
    const bid = r.branch_id as string;
    if (!byBranch.has(bid)) byBranch.set(bid, []);
    byBranch.get(bid)!.push({
      // Fase C: normalize 'Fiado' → 'Customers' when reading from DB
      module: String(r.module) === 'Fiado' ? 'Customers' : String(r.module),
      can_view: Boolean(r.can_view),
      can_edit: Boolean(r.can_edit),
      can_void: Boolean((r as { can_void?: boolean }).can_void),
      can_discount: Boolean((r as { can_discount?: boolean }).can_discount),
      can_manage_agenda: Boolean((r as { can_manage_agenda?: boolean }).can_manage_agenda),
      can_view_others:
        (r as { can_view_others?: boolean | null }).can_view_others == null
          ? true
          : Boolean((r as { can_view_others?: boolean }).can_view_others),
      actions: parsePermissionActions((r as { actions?: unknown }).actions),
    });
  }

  const branchIds = [...byBranch.keys()];
  recordStartupNetworkCall('branches', 'branches?employee');
  const { data: branchRows, error: brErr } = await supabase
    .from('branches')
    .select('id,name,address,stock_sale_policy,sellable_modifier_assumption,barcode_input_mode,scale_barcode_profile,manual_discount_max_percent,manual_discount_max_amount,kitchen_fire_mode,kitchen_auto_archive_on_delivered,is_active')
    .in('id', branchIds)
    .eq('is_active', true);
  if (brErr) throw brErr;

  const branchById = new Map(
    (branchRows ?? []).map(
      (b: {
        id: string;
        name: string;
        address?: string;
        stock_sale_policy?: string;
        barcode_input_mode?: string;
        scale_barcode_profile?: string;
        manual_discount_max_percent?: number;
        manual_discount_max_amount?: number;
        kitchen_fire_mode?: string;
        is_active?: boolean;
      }) => [b.id, b]
    )
  );

  const out: BranchAccess[] = [];
  for (const [branchId, mods] of byBranch) {
    const b = branchById.get(branchId);
    if (!b) continue;
    const modMap = new Map(mods.map((m) => [m.module, m]));
    const permissions: BranchPermission[] = MODULE_ORDER.map((module) => {
      const pr = modMap.get(module);
      return {
        module,
        canView: pr?.can_view ?? false,
        canEdit: pr?.can_edit ?? false,
        canVoid: module === 'POS' || module === 'Expenses' || module === 'Cash' ? (pr?.can_void ?? false) : undefined,
        canDiscount: module === 'POS' ? (pr?.can_discount ?? false) : undefined,
        canManageAgenda: module === 'Agenda' ? (pr?.can_manage_agenda ?? false) : undefined,
        canViewOthers: pr?.can_view_others ?? true,
        actions: pr?.actions ?? {},
      };
    });
    out.push({
      id: b.id,
      name: b.name,
      location: b.address ?? 'Dirección no cargada',
      permissions,
      isActive: parseBranchIsActive((b as { is_active?: unknown }).is_active),
      stockSalePolicy: parseStockSalePolicy((b as { stock_sale_policy?: unknown }).stock_sale_policy),
      barcodeInputMode: parseBarcodeInputMode((b as { barcode_input_mode?: unknown }).barcode_input_mode),
      scaleBarcodeProfile: parseScaleBarcodeProfile((b as { scale_barcode_profile?: unknown }).scale_barcode_profile),
      manualDiscountMaxPercent: parseManualDiscountNumber(
        (b as { manual_discount_max_percent?: unknown }).manual_discount_max_percent,
        20
      ),
      manualDiscountMaxAmount: parseManualDiscountNumber(
        (b as { manual_discount_max_amount?: unknown }).manual_discount_max_amount,
        5000
      ),
      kitchenFireMode: parseKitchenFireMode((b as { kitchen_fire_mode?: unknown }).kitchen_fire_mode),
      kitchenAutoArchiveOnDelivered: parseKitchenAutoArchiveOnDelivered(
        (b as { kitchen_auto_archive_on_delivered?: unknown }).kitchen_auto_archive_on_delivered,
      ),
      sellableModifierAssumption: parseSellableModifierAssumption(
        (b as { sellable_modifier_assumption?: unknown }).sellable_modifier_assumption,
      ),
    });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

let branchesInFlight: Promise<BranchAccess[]> | null = null;

export const fetchUserBranches = async (options?: { force?: boolean }): Promise<BranchAccess[]> => {
  const now = Date.now();
  if (!options?.force) {
    const cached = getUserBranchesCacheIfFresh(now);
    if (cached) {
      debugServiceFetchWarn('[branches] retornando cache');
      return cached;
    }
  }
  if (branchesInFlight) {
    debugServiceFetchWarn('[branches] coalescando con in-flight');
    return branchesInFlight;
  }

  debugServiceFetchWarn('[branches] fetch remoto');

  branchesInFlight = fetchUserBranchesRemote().finally(() => {
    branchesInFlight = null;
  });
  return branchesInFlight;
};

async function fetchUserBranchesRemote(): Promise<BranchAccess[]> {
  const token = await ensureFreshAccessToken();
  if (!token) return [];

  const { session } = useAuthStore.getState();
  if (!session) return [];
  const list = await fetchBranchesForSessionUser(session.user.id);
  try {
    await clearLegacyLocalStockSalePolicy();
  } catch {
    /* ignore */
  }
  setUserBranchesCache(list);
  return list;
}
